import Tabs from "./tabs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import DataTableWithCheckbox from '../../../components/dataTableWithSearch/DataTableWithCheckBox';
import { Box, Stack, Typography, Dialog, DialogContent } from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { qrDialogPaperSx, QrModalHeader } from "../../qrCode/components/qrUi";
import { ContactCreateForm } from "./contactCreation";
import FilterMultiSelect from "../../../components/selections/filterMultiSelect";
import ContactsIcon from "@mui/icons-material/Contacts";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { QR } from "../../qrCode/components/qrUi";
import { NameCell, ChipList, MutedText, filterBoxSx } from "../../leadMangement/components/leadUi";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { useNavigate } from "react-router-dom";
import { getIndividualUserThunk } from "../contactThunk";
import ContactProfileTabModal from '../../contactProfile/contactProfileTabModal';

export default function ContactList() {
    const { getContacts, getTags, getOrganizations, status1, error } = useSelector((state) => state.contacts);
    
    const { branchDepartments } = useSelector((state) => state.general);
    const dispatch = useDispatch();
    const navigate = useNavigate();


    // 🔹 Helper: Filter master list by IDs that exist in contacts
    const filterByUsedIds = (masterList, contacts, contactKey) => {
        if (!masterList || masterList.length === 0) return [];

        const usedIds = new Set();
        contacts?.forEach((c) => {
            const ids = (c[contactKey] || "").split(",").map(x => x.trim()).filter(Boolean);
            ids.forEach(id => usedIds.add(id));
        });

        // Only include used items
        return masterList.filter(item => usedIds.has(String(item.id)));
    };

    // 🔹 Branch Department options
    const branchDeptOptions = useMemo(() => {
        if (!getContacts || getContacts.length === 0) return [];

        let options = [];

        if (branchDepartments && branchDepartments.length > 0) {
            const filtered = filterByUsedIds(branchDepartments, getContacts, "branch_department_id");
            options = filtered.map(bd => ({
                value: String(bd.id),
                label: bd.branch_department_name || bd.branch_department_name || `Branch ${bd.id}`,
            }));
        }

        // ✅ Add Un Assign if there are unassigned contacts
        const hasUnassigned = getContacts.some(c => !c.branch_department_id || c.branch_department_id === "0");
        if (hasUnassigned) options.unshift({ value: "0", label: "Un Assign" });

        return options;
    }, [branchDepartments, getContacts]);


    // 🔹 Tag options
    const uniqueTags = useMemo(() => {
        if (!getContacts || getContacts.length === 0) return [];

        let options = [];

        if (getTags && getTags.length > 0) {
            const filtered = filterByUsedIds(getTags, getContacts, "tag_id");
            options = filtered.map(tag => ({
                value: String(tag.id),
                label: tag.tag_name || tag.name || `Tag ${tag.id}`,
            }));
        }

        const hasUnassigned = getContacts.some(c => !c.tag_id || c.tag_id === "0");
        if (hasUnassigned) options.unshift({ value: "0", label: "Un Assign" });

        return options;
    }, [getTags, getContacts, getOrganizations]);

    // 🔹 Organization options (separate filter)
    const orgOptions = useMemo(() => {
        if (!getContacts || getContacts.length === 0) return [];

        let options = [];
        if (getOrganizations && getOrganizations.length > 0) {
            // contacts may store multiple organization ids in the `organization_ids` field
            const filtered = filterByUsedIds(getOrganizations, getContacts, "organization_ids");
            options = filtered.map(org => ({
                value: String(org.id),
                label: org.organization_name || org.name || `Org ${org.id}`,
            }));
        }

        // consider both `organization_ids` and legacy `organization_id` when checking unassigned
        const hasUnassigned = getContacts.some(c => {
            const val = (c.organization_ids ?? c.organization_id);
            return !val || String(val).trim() === "" || String(val) === "0";
        });
        if (hasUnassigned) options.unshift({ value: "0", label: "Un Assign" });

        return options;
    }, [getOrganizations, getContacts]);

    const [state, setState] = useState({
        selectedBrachDeptValue: branchDeptOptions,
        selectedTagValue: uniqueTags,
        selectedOrganizationValue: orgOptions,
        openContactEditModal: false,
        selectedRowData: null,
    });

    const { selectedBrachDeptValue, selectedTagValue, selectedOrganizationValue, openContactEditModal, selectedRowData } = state;

    // 🔹 Filter logic — now handles “Un Assign” correctly
    const filterContacts = (contacts, key, selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) return [];

        const selectedValues = selectedOptions.map(opt => opt.value);
        const includeUnassigned = selectedValues.includes("0");

        return contacts.filter(contact => {
            const contactValue = contact[key];
            const hasValue = !!(contactValue && contactValue.trim() !== "");

            // ✅ If "Un Assign" is selected and contact has no value → include
            if (includeUnassigned && !hasValue) return true;

            // ✅ If unassigned + others selected → include both types
            if (includeUnassigned && hasValue) {
                const ids = contactValue.split(",").map(x => x.trim());
                return ids.some(id => selectedValues.includes(id));
            }

            // ✅ Otherwise, filter normally
            if (hasValue) {
                const ids = contactValue.split(",").map(x => x.trim());
                return ids.some(id => selectedValues.includes(id));
            }

            return false;
        });
    };

    // 🔹 Filter contacts for display
    const filteredContacts = useMemo(() => {
        if (!getContacts || getContacts.length === 0) return [];

        let filtered = filterContacts(getContacts, "branch_department_id", selectedBrachDeptValue);
        if (filtered.length === 0) return [];

        // Apply organization filter (contacts use `organization_ids` for multi-values)
        filtered = filterContacts(filtered, "organization_ids", selectedOrganizationValue);
        if (filtered.length === 0) return [];

        // Then apply tag filter
        filtered = filterContacts(filtered, "tag_id", selectedTagValue);
        return filtered;
    }, [getContacts, selectedBrachDeptValue, selectedOrganizationValue, selectedTagValue]);

    const [createOpen, setCreateOpen] = useState(false);

    // 🔹 Snackbar
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // 🔹 Columns
    const columns = useMemo(() => [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "name",
            header: "Name",
            Cell: ({ row }) => (
                <NameCell
                    name={row.original.name}
                    title="View contact profile"
                    onClick={() => {
                        // navigate(APP_ROUTES.PROFILE);
                        setState(prev => ({ ...prev, openContactEditModal: true, selectedRowData: row?.original }));
                        dispatch(getIndividualUserThunk({ wa_contact_id: row.original.id }));
                    }}
                />
            )
        },
        // { accessorKey: "email", header: "Email" },
        {
            accessorKey: "mobile", header: "Mobile", Cell: ({ row }) => row.original.mobile ? (
                <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: "#475569" }}>
                    <PhoneIphoneIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{row.original.mobile}</Typography>
                </Stack>
            ) : <Typography variant="body2" color="#cbd5e1">-</Typography>
        },
        { accessorKey: "department_name", header: "Department", Cell: ({ row }) => <ChipList value={row.original.department_name} color={QR.blue} /> },
        { accessorKey: "branch_name", header: "Branch", Cell: ({ row }) => <MutedText value={row.original.branch_name} /> },
        { accessorKey: "organization_names", header: "Organization", Cell: ({ row }) => <ChipList value={row.original.organization_names} color="#0891b2" /> },
        { accessorKey: "tag_names", header: "Tags", Cell: ({ row }) => <ChipList value={row.original.tag_names} /> },
        {
            accessorKey: "inserted_time",
            header: "Created Date",
            Cell: ({ row }) => <Typography variant="body2" color="text.secondary">{formatDate(row.original.inserted_time)}</Typography>
        }
    ], [navigate, dispatch]);

    // 🔹 Handle dropdown changes
    const handleMultiSelectChange = (name, selectedValues) => {
        setState(prev => ({
            ...prev,
            [name]: selectedValues,
        }));
    };

    // 🔹 Reset selections when data changes
    useEffect(() => {
        setState({
            selectedBrachDeptValue: branchDeptOptions,
            selectedTagValue: uniqueTags,
            selectedOrganizationValue: orgOptions,
        });
    }, [branchDeptOptions, uniqueTags, orgOptions]);

    // 🔹 Render filter dropdowns
    const renderQrFilter = () => (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
            <Box sx={filterBoxSx}>
                <FilterMultiSelect
                    name="selectedBrachDeptValue"
                    label="Branch Dept"
                    options={branchDeptOptions}
                    value={selectedBrachDeptValue || []}
                    onChange={handleMultiSelectChange}
                />
            </Box>

            <Box sx={filterBoxSx}>
                <FilterMultiSelect
                    name="selectedTagValue"
                    label="Tag"
                    options={uniqueTags}
                    value={selectedTagValue || []}
                    onChange={handleMultiSelectChange}
                />
            </Box>

            <Box sx={filterBoxSx}>
                <FilterMultiSelect
                    name="selectedOrganizationValue"
                    label="Organization"
                    options={orgOptions}
                    value={selectedOrganizationValue || []}
                    onChange={handleMultiSelectChange}
                />
            </Box>
        </Box>
    );

    // 🔹 Snackbar for errors
    useEffect(() => {
        if (status1 === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status1, error]);

    // Summary cards (display only, from the loaded contacts)
    const all = getContacts || [];
    const now = new Date();
    const stats = [
        { label: "Total Contacts", value: all.length, icon: <ContactsIcon />, color: QR.main },
        { label: "Tagged", value: all.filter(c => c.tag_id && c.tag_id !== "0").length, icon: <LocalOfferOutlinedIcon />, color: QR.purple },
        { label: "No Branch Dept", value: all.filter(c => !c.branch_department_id || c.branch_department_id === "0").length, icon: <ApartmentIcon />, color: QR.amber },
        {
            label: "Added This Month", value: all.filter(c => {
                const d = new Date(c.inserted_time);
                return !Number.isNaN(d.getTime()) && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length, icon: <EventAvailableIcon />, color: QR.blue
        },
    ];

    return (
        <div>
            <Tabs stats={stats} onCreate={() => setCreateOpen(true)}>
                <DataTableWithCheckbox
                    columns={columns}
                    data={filteredContacts || []}
                    renderFilters={renderQrFilter()}
                    status={status1}
                    searchFirst
                    emptyMessage="No contacts match these filters."
                />
            </Tabs>



            {/* Add contact popup */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: { sm: 860 } } }}
            >
                <QrModalHeader
                    icon={<PersonAddAlt1Icon />}
                    title="Add Contact"
                    subtitle="Add one contact, or upload many from Excel"
                    onClose={() => setCreateOpen(false)}
                />
                <DialogContent sx={{ p: 0 }}>
                    {/* remounts on each open, so the form starts empty */}
                    {createOpen && (
                        <ContactCreateForm
                            embedded
                            onCancel={() => setCreateOpen(false)}
                            onSuccess={(message) => {
                                setCreateOpen(false);
                                setSnackbar({ open: true, message: message || "Contact created", severity: "success" });
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />



            <ContactProfileTabModal
                show={state.openContactEditModal}
                handleClose={() => setState(prev => ({ ...prev, openContactEditModal: false }))}
                rowData={selectedRowData}
                type="contacts"
            />
        </div>
    );
}
